import React, { useEffect, useRef } from 'react';
import { browserDetect } from 'utils/helpers';

const Input = ({
  htmlFor,
  children,
  onUploadImages,
  clockOut,
  disabled,
  acceptPdf,
  // Fires as the file picker is about to open. The input is display:none and
  // driven by a <label htmlFor>, whose click is forwarded here as a synthetic
  // click, so this is the one moment the app can observe a capture starting —
  // which is the only evidence available when the page dies before any file
  // comes back. See utils/uploadDiagnostics.
  onPickerOpen,
  // Fires when the user dismisses the picker without choosing anything. React
  // has no synthetic event for this, so it is attached natively below.
  onPickerCancel,
}) => {
  const inputRef = useRef(null);

  // `cancel` is the only signal that a picker closed empty — `change` does not
  // fire when the user backs out. Without it every abandoned picker would look
  // exactly like a capture the page died during, and the false positives would
  // drown the signal. Not universally supported (Chrome 113+, Firefox 91+,
  // Safari 16.4+); where it is missing, the staleness rule in
  // uploadDiagnostics is the fallback.
  //
  // onPickerCancel is a module-level import at its only call site, so this binds
  // once and never re-runs. Do not give the input below a changing `key`: the
  // remount would leave the listener on the detached node and silently restore
  // the false positives. Consumers clear the input with `input.value = ''` in
  // their change handler, which needs no remount.
  useEffect(() => {
    const element = inputRef.current;
    if (!element || !onPickerCancel) return undefined;

    element.addEventListener('cancel', onPickerCancel);
    return () => element.removeEventListener('cancel', onPickerCancel);
  }, [onPickerCancel]);

  const browser = browserDetect();
  // console.log('children', children);
  // console.log('browser', browser);

  const baseAccept = clockOut
    ? 'image/*, image/heic, image/heif'
    : 'image/*, video/*, image/heic, image/heif';

  // The invoice scan reads a PDF as readily as a photo, so wherever it is on, a
  // PDF invoice must not need the picker's "All files" escape hatch first.
  const accept = acceptPdf ? `${baseAccept}, application/pdf` : baseAccept;

  // Chrome and Chromium Edge (the /chrome/i test in browserDetect matches Edge
  // first) deliberately get NO `accept` attribute. This is load-bearing, not an
  // oversight, and it is the reason the two branches below diverge from the one
  // at the bottom of the file.
  //
  // On Android 13+ a media `accept` value routes Chrome to the Android Photo
  // Picker, which has no camera tile — so adding one here does not refine the
  // picker, it removes the ability to take a photo at all on Android 14/15. A
  // bare <input type="file"> is the only combination that still offers Camera
  // alongside the file browser. This was found the hard way on a device in
  // March 2024 across five commits (157747c35, 1fdc9420c, c404b5cfe, b1f631989,
  // 73704ce38), which tried `accept="image/*"`, then `accept="image/*,
  // capture=camera"` — never valid, `capture` is its own attribute — before
  // settling on none.
  //
  // If a filter is ever genuinely needed here, the escape hatch is the
  // non-standard `accept="image/*,android/allowCamera"`; it is undocumented
  // Chrome behaviour and could be withdrawn without notice.
  //
  // Note this is unrelated to the intermittent Android stall where the camera
  // never shows its confirm screen. That was reproduced on a bare page with no
  // app code and is a fault inside the system camera app, unreachable from
  // here — no attribute on this element affects it.
  if (browser === 'Google Chrome') {
    if (clockOut) {
      return (
        <React.Fragment>
          <input
            id={htmlFor}
            onChange={onUploadImages}
            ref={inputRef}
            onClick={onPickerOpen}
            style={{ display: 'none' }}
            type="file"
            multiple="multiple"
            disabled={disabled}

          // alt="Add image"
          />
          {children}
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <input
          id={htmlFor}
          onChange={onUploadImages}
          ref={inputRef}
          onClick={onPickerOpen}
          style={{ display: 'none' }}
          type="file"
          multiple="multiple"
          disabled={disabled}
        />

        {children}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <input
        accept={accept}
        id={htmlFor}
        onChange={onUploadImages}
        ref={inputRef}
        onClick={onPickerOpen}
        style={{ display: 'none' }}
        type="file"
        multiple="multiple"
        alt="Add image"
        disabled={disabled}
      />

      {children}
    </React.Fragment>
  );
};

export default Input;
